import type { FormType, StyleFullValue } from '@vedalib/editor/lib/brand'
import cn from 'classnames'
import { t } from 'i18next'
import * as R from 'ramda'
import { useCallback, useMemo, useState } from 'react'
import React from 'react'

import FieldLayout from 'components/controls/Field/Layout'
import { useFormDefaultValues, useFormOnChangeValue, useFormValues } from 'components/formKit/hooks'
import type { IFieldBaseRenderProps } from 'components/formKit/types'
import Button from 'components/uiKit/Button'
import Dropdown from 'components/uiKit/Dropdown'
import Icon from 'components/uiKit/Icon'
import { KitSize } from 'components/uiKit/KitTypes'
import Segmented from 'components/uiKit/Segmented'

import * as s from './LabelBrandDropdown.module.scss'

const LabelBrandDropdown: React.FC<IFieldBaseRenderProps> = ({ config: { name, label } }) => {
  const [isVisible, setIsVisible] = useState(false)
  const value = useFormValues<StyleFullValue>(name)
  const defaultValue = useFormDefaultValues<StyleFullValue>(name)
  const isActive = !R.equals(value, defaultValue)
  const formValue = useMemo(() => value?.form || defaultValue?.form, [defaultValue, value])

  const onChangeValue = useFormOnChangeValue()

  const handleReset = useCallback(() => {
    setIsVisible(false)
    return onChangeValue?.({
      name,
      value: defaultValue,
      meta: { dirty: true, touched: true },
    })
  }, [defaultValue, name, onChangeValue])

  const onChangeForm = useCallback(
    (val: boolean, formValue: FormType) => {
      const currentForm = value?.form || defaultValue?.form || []
      const updatedForm = val
        ? [...currentForm, formValue]
        : currentForm.filter((item: string) => item !== formValue)

      return onChangeValue?.({
        name,
        value: { ...value, form: updatedForm },
        meta: { dirty: true },
      })
    },
    [defaultValue?.form, name, onChangeValue, value],
  )

  if (!label) {
    return null
  }

  return (
    <Dropdown
      name={name}
      onVisibleChange={setIsVisible}
      overlay={
        <div className={s.root}>
          <div className={s.title}>{t('page.brand.settings.header')}</div>
          <FieldLayout
            info={t('page.editor.workWithGrid.info')}
            label={t('page.editor.workWithGrid.label')}
            layout='horizontal'
            name={`${name}.edit`}
          >
            <Segmented
              name={`${name}.edit`}
              onChange={(val) => onChangeForm(val, 'edit')}
              value={Boolean(formValue?.includes('edit'))}
            />
          </FieldLayout>
          <FieldLayout
            info={t('page.editor.workWithContent.info')}
            label={t('page.editor.workWithContent.label')}
            layout='horizontal'
            name={`${name}.fill`}
          >
            <Segmented
              name={`${name}.fill`}
              onChange={(val) => onChangeForm(val, 'fill')}
              value={Boolean(formValue?.includes('fill'))}
            />
          </FieldLayout>
          {Boolean(defaultValue) && (
            <div className={s.reset}>
              <div className={s.resetLabel}>{t('page.brand.returnThemeToBase')}</div>
              <Button
                disabled={!isActive}
                name={`${name}-Reset`}
                onClick={handleReset}
                size={KitSize.S}
                styleType='ghost'
                type='button'
              >
                <Icon name='otherReload' size={KitSize.S} />
              </Button>
            </div>
          )}
        </div>
      }
      placement='top-start'
      styleType='clear'
      visible={isVisible}
    >
      <div className={cn(s.label, { [s.value]: isActive, [s.visible]: isVisible })}>
        {label}
        {isActive && <div className={s.indicator} />}
      </div>
    </Dropdown>
  )
}

export default LabelBrandDropdown
